import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridPaginationModel, GridRowParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { IEventSFilter } from 'src/@types/SearchFilterTypes';
import CDataGridPro from 'src/components/CDataGridPro';
import { CGridFooter } from 'src/components/custom-grid-data-components';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { IEventGridRow } from 'src/hooks/useGetGridCol';
import { ContentBox, ContentBoxButtons, ContentButtonCreate } from 'src/layouts/form/FormButton';
import { IEvent } from 'src/models';
import { INIT_PAGINATION, IPagination } from 'src/models/extensions/withPagination';
import { eventApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';

import { EventFormDialog } from './EventFormDialog';

interface IEventGridProps {
  searchData: Partial<IEventSFilter>;
}

export const EventGrid = ({ searchData }: IEventGridProps) => {
  const { t } = useTranslation();
  const { getEventGridCol } = useGetGridCol();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { openDialog, closeDialog } = useDialogContext();
  // 그리드 row 데이터
  const [gridRows, setGridRows] = useState<IEventGridRow[]>([]);
  // 페이지네이션
  const [pagination, setPagination] = useState<IPagination>(INIT_PAGINATION);

  // 사업 목록 조회
  const getEventData = useCallback(async () => {
    const formData = {
      ...searchData,
      page: pagination.page,
      size: pagination.size,
    };
    await eventApi.getEvent(formData).then((res) => {
      if (res) {
        const newGridRows = res.data.map((event, i) => {
          return { ...event, id: event.eventSid ?? i };
        });
        setGridRows(newGridRows as IEventGridRow[]);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      }
    });
  }, [searchData, pagination.page, pagination.size]);

  useEffect(() => {
    getEventData();
  }, [getEventData]);

  // 등록 버튼: 등록 dialog open
  const handleAdd = () => {
    openDialog({
      id: FORM_IDS.EVENT,
      formType: FormType.CREATE,
      dialogContent: (
        <EventFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<IEventGridRow>) => {
    callApiWithLoading(async () => {
      await eventApi.getDetail(param.row.eventSid).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.EVENT,
            formType: FormType.UPDATE,
            dialogContent: (
              <EventFormDialog
                formType={FormType.UPDATE}
                onSubmitSuccess={handleSubmitSuccess}
                selectedData={res.data as IEvent}
              />
            ),
          });
        }
      });
    });
  };

  // 등록 & 수정 성공 시
  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.EVENT);
    getEventData();
  };

  // 페이지 모델 변경 시
  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    setPagination({
      ...pagination,
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
    });
  };

  return (
    <ContentBox>
      <ContentBoxButtons>
        <ContentButtonCreate onClick={handleAdd}>{t('common.button.register')}</ContentButtonCreate>
      </ContentBoxButtons>
      <CDataGridPro
        columns={getEventGridCol()}
        rows={gridRows}
        rowCount={toJS(pagination.totalElements)}
        paginationModel={{
          page: pagination.page - 1,
          pageSize: pagination.size,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        onRowDoubleClick={handleRowDoubleClick}
        slots={{ footer: CGridFooter }}
      />
    </ContentBox>
  );
};

export default observer(EventGrid);
